import {createEntityAdapter, EntityAdapter, EntityState, Update} from '@ngrx/entity';
import {CourseSection} from '../models/course-section.model';
import {compareSections} from '../common/sort-model';
import {createReducer, on} from '@ngrx/store';
import {CourseSectionActions} from './action-types';


export interface SectionsPageInfo {
  lastSeqNoLoaded: number;
  firstSeqNoLoaded: number;
  allLoaded: boolean;
  firstSectionLoaded: boolean;
  landingPageLoaded: boolean;
}

export interface State extends EntityState<CourseSection> {
  sectionsPageInfo: { [courseId: string]: SectionsPageInfo };
  pendingSectionsReordering: Update<CourseSection>[];
}


export const adapter: EntityAdapter<CourseSection> = createEntityAdapter<CourseSection>({
  sortComparer: compareSections
});


export const initialState: State = adapter.getInitialState({
  sectionsPageInfo: {},
  pendingSectionsReordering: []
});

export const courseSectionsReducer = createReducer(
  initialState,

  on(CourseSectionActions.addCourseSection, (state, {courseSection}) => adapter.addOne(courseSection, state)),

  on(CourseSectionActions.courseSectionsLoaded, (state, {courseSections, courseId}) => {
    const seqNos = courseSections.map(s => s.seqNo);
    const maxSeq = seqNos.length > 0 ? Math.max(...seqNos) : 0;
    const minSeq = seqNos.length > 0 ? Math.min(...seqNos) : 1;
    return {
      ...adapter.addMany(courseSections, state),
      sectionsPageInfo: {
        ...state.sectionsPageInfo,
        [courseId]: {
          lastSeqNoLoaded: maxSeq,
          firstSeqNoLoaded: minSeq,
          allLoaded: true,
          firstSectionLoaded: minSeq === 1,
          landingPageLoaded: true
        }
      },
      pendingSectionsReordering: state.pendingSectionsReordering
    };
  }),

  on(CourseSectionActions.courseSectionPageLoaded, (state, { courseSections, courseId, allLoaded, direction }) => {
    const existing = state.sectionsPageInfo[courseId];
    const seqNos = courseSections.map(s => s.seqNo);
    const isLanding = direction === 'landing';
    const newLast = seqNos.length > 0
      ? Math.max(existing?.lastSeqNoLoaded ?? 0, ...seqNos)
      : (existing?.lastSeqNoLoaded ?? 0);
    const newFirst = seqNos.length > 0
      ? (isLanding ? Math.min(...seqNos) : Math.min(existing?.firstSeqNoLoaded ?? Infinity, ...seqNos))
      : (existing?.firstSeqNoLoaded ?? 0);
    const newFirstSectionLoaded =
      newFirst === 1 ||
      (isLanding ? false : (existing?.firstSectionLoaded ?? false)) ||
      (direction === 'prev' && seqNos.length === 0);
    const newLandingPageLoaded = (existing?.landingPageLoaded ?? false) || isLanding;
    // Preserve allLoaded:true if already known — don't let a landing page re-load override it
    const newAllLoaded = (existing?.allLoaded ?? false) ? true : allLoaded;
    return {
      ...adapter.addMany(courseSections, state),
      sectionsPageInfo: {
        ...state.sectionsPageInfo,
        [courseId]: {
          lastSeqNoLoaded: newLast,
          firstSeqNoLoaded: newFirst,
          allLoaded: newAllLoaded,
          firstSectionLoaded: newFirstSectionLoaded,
          landingPageLoaded: newLandingPageLoaded
        }
      },
      pendingSectionsReordering: state.pendingSectionsReordering
    };
  }),

  on(CourseSectionActions.updateCourseSection, (state, {courseSection}) => adapter.updateOne(courseSection, state)),

  on(CourseSectionActions.deleteCourseSection, (state, {id}) => adapter.removeOne(id, state)),

  on(CourseSectionActions.updateSectionOrder, (state, {newSortOrder}) => {

    const newSortedSections = newSortOrder;

    const reorderSections: Update<CourseSection>[] = [];

    let seqNoCounter = 1;

    newSortedSections.forEach(section => {

      if (section.seqNo != seqNoCounter) {

        const changes: Partial<CourseSection> = {
          seqNo: seqNoCounter
        };

        reorderSections.push({id:section.id, changes});
      }

      seqNoCounter+=1;

    });

    return adapter.updateMany(reorderSections, {...state, pendingSectionsReordering: reorderSections});

  }),

  on(CourseSectionActions.updateSectionOrderCompleted, state => {
    return {
      ...state,
      pendingSectionsReordering: []
    };
  })

);



export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = adapter.getSelectors();
